import { useState, useRef } from "react";
import { api, getMac } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Link2, KeyRound } from "lucide-react";

export default function PlaylistForm({ onDone, mac: macProp }) {
  const [tab, setTab] = useState("m3u");
  const [name, setName] = useState("My Playlist");
  const [url, setUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const mac = macProp || getMac();

  const stopPoll = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };

  const poll = async (jobId) => {
    try {
      const { data } = await api.get(`/device/${mac}/import/${jobId}`);
      setJob(data);
      if (data.status === "done") {
        stopPoll(); setLoading(false);
        const c = data.counts || {};
        toast.success(`Playlist added! TV: ${c.live || 0}, Movies: ${c.movie || 0}, Series: ${c.series || 0}`);
        setJob(null);
        onDone?.();
        return;
      }
      if (data.status === "error") {
        stopPoll(); setLoading(false); setJob(null);
        toast.error(data.error || "Error importing playlist");
        return;
      }
    } catch (e) {
      // transient poll error — keep trying a bit longer
    }
    pollRef.current = setTimeout(() => poll(jobId), 700);
  };

  const submit = async () => {
    setLoading(true);
    setJob({ status: "queued", stage: "Starting…", pct: 0, current: 0, total: 0 });
    try {
      const payload = tab === "m3u"
        ? { name, type: "m3u", url }
        : { name, type: "xtream", url, username, password };
      const { data } = await api.post(`/device/${mac}/playlist/import`, payload);
      poll(data.job_id);
    } catch (e) {
      setLoading(false); setJob(null);
      toast.error(e.response?.data?.detail || "Error adding playlist");
    }
  };

  const input = "w-full px-4 py-3 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 transition-colors";
  const pct = Math.max(0, Math.min(100, job?.pct || 0));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-zinc-900/70">
        {[["m3u", "M3U Link", Link2], ["xtream", "Xtream Codes", KeyRound]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} disabled={loading}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-semibold transition-colors ${tab === k ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <input data-testid="playlist-name-input" className={input} placeholder="Playlist name" value={name} onChange={(e) => setName(e.target.value)} disabled={loading} />

      {tab === "m3u" ? (
        <input data-testid="m3u-url-input" className={input} placeholder="Any playlist URL — .m3u, .m3u8, get.php, /playlist/user/pass, …" value={url} onChange={(e) => setUrl(e.target.value)} disabled={loading} />
      ) : (
        <>
          <input data-testid="xtream-host-input" className={input} placeholder="Server (http://host:port)" value={url} onChange={(e) => setUrl(e.target.value)} disabled={loading} />
          <input data-testid="xtream-user-input" className={input} placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading} />
          <input data-testid="xtream-pass-input" className={input} placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
        </>
      )}

      {job && loading && (
        <div data-testid="import-progress" className="rounded-lg bg-zinc-900 border border-zinc-800 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span data-testid="import-stage" className="font-semibold text-zinc-200 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-red-500" /> {job.stage || "Working…"}
            </span>
            <span data-testid="import-pct" className="font-mono-data text-red-400">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
            <div className="h-full bg-red-600 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
          {job.total > 0 && (
            <p data-testid="import-count" className="text-xs text-zinc-500 font-mono-data">
              {Number(job.current || 0).toLocaleString()} / {Number(job.total || 0).toLocaleString()} items
            </p>
          )}
        </div>
      )}

      <button data-testid="submit-playlist-btn" onClick={submit} disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-semibold transition-colors">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Add Playlist"}
      </button>
    </div>
  );
}
